import { Box, Grid, Typography } from "@mui/material";
import { AddButton } from "~/components/add-button";
import { IntegrationHeaderDatabox } from "~/pages/integrations/headers/IntegrationHeaderDatabox";
import { IntegrationType } from "~/operations";
import {
  AmazonS3Icon,
  AwsIcon,
  AzureDevopsIcon,
  AzureIcon,
  BigqueryIcon,
  CrowdstrikeIcon,
  DomainIcon,
  FlyingEnvelopeIcon,
  GcpIcon,
  GcsBucketIcon,
  GithubIcon,
  GitlabIcon,
  GoogleWorkspaceIcon,
  JiraIcon,
  KubernetesIcon,
  Ms365Icon,
  MsftDefenderIcon,
  OciIcon,
  OktaIcon,
  PostgreSqlIcon,
  SecurityPipelineIcon,
  SentinelOneIcon,
  ServiceNowIcon,
  ShodanIcon,
  SlackIcon,
  SnowflakeIcon,
  WebhookIcon,
  WorkstationIcon,
  ZendeskIcon,
} from "~/icons";
import { OrganizationScope, ScopeType, SpaceScope } from "~/hooks/useScope";
import { CanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { JSX } from "react";

interface IntegrationsListHeaderProps {
  scope: SpaceScope | OrganizationScope;
  type: IntegrationType;
  hasIntegrations: boolean;
}

export function IntegrationsListHeader({
  scope,
  type,
  hasIntegrations,
}: IntegrationsListHeaderProps) {
  const getContent = () => {
    let title: string;
    let tagline: string;
    let icon: JSX.Element;
    let href: string;

    switch (type) {
      case IntegrationType.Aws:
        title = "AWS";
        tagline = "Cloud Hosting";
        icon = <AwsIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/aws?${scope.params}`;
        break;

      case IntegrationType.Azure:
        title = "Azure";
        tagline = "Cloud Hosting";
        icon = <AzureIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/azure?${scope.params}`;
        break;

      case IntegrationType.CrowdstrikeFalcon:
        title = "CrowdStrike";
        tagline = "3rd Party Data Source";
        icon = <CrowdstrikeIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/crowdstrike?${scope.params}`;
        break;

      case IntegrationType.SentinelOne:
        title = "SentinelOne";
        tagline = "3rd Party Data Source";
        icon = <SentinelOneIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/sentinelone?${scope.params}`;
        break;

      case IntegrationType.AzureBlob:
        title = "Azure Blob Storage";
        tagline = "Data Export";
        icon = <AzureIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/azure_blob?${scope.params}`;
        break;

      case IntegrationType.MicrosoftDefender:
        title = "Microsoft Defender for Cloud";
        tagline = "3rd Party Data Source";
        icon = <MsftDefenderIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/defender?${scope.params}`;
        break;

      case IntegrationType.Bigquery:
        title = "BigQuery";
        tagline = "Data Export";
        icon = <BigqueryIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/bigquery?${scope.params}`;
        break;

      case IntegrationType.Shodan:
        title = "Shodan";
        tagline = "Networking";
        icon = <ShodanIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/shodan?${scope.params}`;
        break;

      case IntegrationType.Snowflake:
        title = "Snowflake";
        tagline = "Data Export";
        icon = <SnowflakeIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/snowflake?${scope.params}`;
        break;

      case IntegrationType.K8S:
        title = "Kubernetes";
        tagline = "Cloud Hosting";
        icon = <KubernetesIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/kubernetes?${scope.params}`;
        break;

      case IntegrationType.Host:
        title = "Domain/IP Address";
        tagline = "";
        icon = <DomainIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/host?${scope.params}`;
        break;

      case IntegrationType.Postgres:
        title = "PostgreSQL";
        tagline = "Data Export";
        icon = <PostgreSqlIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/postgres?${scope.params}`;
        break;

      case IntegrationType.AwsS3:
        title = "Amazon S3";
        tagline = "Data Export";
        icon = <AmazonS3Icon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/amazons3?${scope.params}`;
        break;

      case IntegrationType.S3:
        title = "S3 Compatible Services";
        tagline = "Data Export";
        icon = <AmazonS3Icon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/amazon-s3-compatible?${scope.params}`;
        break;

      case IntegrationType.Github:
        title = "GitHub";
        tagline = "SaaS Platform";
        icon = <GithubIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/github?${scope.params}`;
        break;

      case IntegrationType.HostedSlack:
        title = "Slack";
        tagline = "SaaS Platform";
        icon = <SlackIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/slack?${scope.params}`;
        break;

      case IntegrationType.Gcp:
        title = "Google Cloud Platform";
        tagline = "Cloud Hosting";
        icon = <GcpIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/gcp?${scope.params}`;
        break;

      case IntegrationType.GcsBucket:
        title = "Google Cloud Storage Bucket";
        tagline = "Data Export";
        icon = <GcsBucketIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/gcs_bucket?${scope.params}`;
        break;

      case IntegrationType.GoogleWorkspace:
        title = "Google Workspace";
        tagline = "SaaS Platform";
        icon = <GoogleWorkspaceIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/google_workspace?${scope.params}`;
        break;

      case IntegrationType.Gitlab:
        title = "GitLab";
        tagline = "SaaS Platform";
        icon = <GitlabIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/gitlab?${scope.params}`;
        break;

      case IntegrationType.TicketSystemJira:
        title = "Atlassian Jira";
        tagline = "Ticketing System";
        icon = <JiraIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_jira?${scope.params}`;
        break;

      case IntegrationType.TicketSystemEmail:
        title = "Ticketing via email";
        tagline = "Ticketing System";
        icon = <FlyingEnvelopeIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_email?${scope.params}`;
        break;

      case IntegrationType.TicketSystemZendesk:
        title = "Zendesk";
        tagline = "Ticketing System";
        icon = <ZendeskIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_zendesk?${scope.params}`;
        break;

      case IntegrationType.TicketSystemGithub:
        title = "GitHub Issues";
        tagline = "Ticketing System";
        icon = <GithubIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_github?${scope.params}`;
        break;

      case IntegrationType.TicketSystemGitlab:
        title = "GitLab Issues";
        tagline = "Ticketing System";
        icon = <GitlabIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_gitlab?${scope.params}`;
        break;
      case IntegrationType.TicketSystemAzureDevops:
        title = "Azure DevOps";
        tagline = "Ticketing System";
        icon = <AzureDevopsIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_azure_devops?${scope.params}`;
        break;

      case IntegrationType.TicketSystemServiceNow:
        title = "ServiceNow";
        tagline = "Ticketing System";
        icon = <ServiceNowIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_servicenow?${scope.params}`;
        break;

      case IntegrationType.TicketSystemWebhook:
        title = "Webhook ticketing";
        tagline = "Ticketing System";
        icon = <WebhookIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ticket_system_webhook?${scope.params}`;
        break;

      case IntegrationType.Oci:
        title = "Oracle Cloud Infrastructure";
        tagline = "Cloud Hosting";
        icon = <OciIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/oci?${scope.params}`;
        break;

      case IntegrationType.Ms365:
        title = "Microsoft 365";
        tagline = "SaaS Platform";
        icon = <Ms365Icon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/ms365?${scope.params}`;
        break;

      case IntegrationType.Okta:
        title = "Okta";
        tagline = "SaaS Platform";
        icon = <OktaIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add/mondoo/okta?${scope.params}`;
        break;

      case IntegrationType.ManagedClient:
        title = "Managed Clients";
        tagline = "";
        icon = <WorkstationIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add?${scope.params}#serverendpointsecurity`;
        break;

      case IntegrationType.SecurityPipelineGithub:
        title = "Security Pipeline";
        tagline = "";
        icon = <SecurityPipelineIcon sx={{ fontSize: 48 }} />;
        href = `/organization/integrations/security_pipeline_github?${scope.params}`;
        break;

      default:
        title = type;
        tagline = "";
        icon = <WorkstationIcon sx={{ fontSize: 48 }} />;
        href = `/space/integrations/add?${scope.params}`;
    }

    return { title, tagline, icon, href };
  };

  const types = [type];
  if (type === IntegrationType.Aws) {
    types.push(IntegrationType.AwsHosted);
  }

  const content = getContent();

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        mb: 1,
      }}
    >
      <Grid
        sx={{
          display: "flex",
          mb: {
            xs: 2,
          },
        }}
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        {content.icon}
        <Box ml={2}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            lineHeight=".9"
            sx={{ textTransform: "uppercase" }}
          >
            {content.title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase" }}
          >
            {content.tagline}
          </Typography>
        </Box>
      </Grid>
      <Box
        sx={{
          display: "flex",
          placeContent: "center",
        }}
      >
        {type !== IntegrationType.Bigquery &&
          hasIntegrations &&
          scope.type !== ScopeType.Organization && (
            <IntegrationHeaderDatabox {...{ scope, types }} />
          )}
        {type !== IntegrationType.SecurityPipelineGithub && (
          <CanCreate>
            <AddButton
              id={`${type}-list`}
              href={content.href}
              layout={{ ml: 6, mt: 0.8 }}
            />
          </CanCreate>
        )}
      </Box>
    </Grid>
  );
}
